import { useItemModifierGroups } from 'src/hooks/useItemModifierGroups';
import { Loader } from 'src/components/Loader';
import { flattenPagesToModifiers, generateModifierFormDefaults, mapModifierGroupToTableRow } from './utils';
import { BundleIcon, Paper, SearchField, Table, Typography } from '@weareopenr/component-library';
import { InfiniteData } from '@tanstack/react-query';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IModifierGroupFilters, ItemModifierGroupsResponseBody } from 'src/api/getModifierGroups';
import { optionGroupCols } from './Table';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';

import {
  BUNDLE_MODIFIER_DESCRIPTION,
  BUNDLE_MODIFIER_FORM_ID,
  BUNDLE_MODIFIER_TITLE,
  CREATE_BUNDLE_OR_MODIFIER_FIRST,
  GO_TO_PRODUCTS,
} from './constants';
import styles from './PriceSchemeOptionGroupOverrides.module.scss';
import EmptyState from '../EmptyState';
import { NoResultsFound } from '../NoResultsFound';
import { useMenus } from 'src/hooks/useMenus';
import { Routes } from 'src/routes';
import { usePricingCategoryFilter } from 'src/hooks/usePricingCategoryFilter';
import { PriceSchemeFilters } from '../PriceSchemeFilters/PriceSchemeFilters';
import { PricingSavePortal } from '../PricingSavePortal';
import { useSavePriceOverrides } from './useSavePriceOverrides';
import { usePriceSchemeFilters } from 'src/hooks/usePriceSchemeFilters';
import { useChannelFilter } from 'src/hooks/useChannelFilter';

const ModifierGroupsEmptyState = ({ hasActiveFilters }: { hasActiveFilters: boolean }) => {
  const navigate = useNavigate();

  if (hasActiveFilters) {
    return (
      <Paper className={styles['empty-state-paper']}>
        <NoResultsFound />
      </Paper>
    );
  }

  return (
    <Paper className={styles['empty-state-paper']}>
      <Typography size="xlarge" weight="semi-bold">
        {BUNDLE_MODIFIER_TITLE}
      </Typography>
      <Typography className={styles['description']}>{BUNDLE_MODIFIER_DESCRIPTION}</Typography>
      <EmptyState
        emptySectionText={CREATE_BUNDLE_OR_MODIFIER_FIRST}
        buttonText={GO_TO_PRODUCTS}
        onClick={() => navigate(Routes.PRODUCT_LIBRARY)}
        Icon={BundleIcon}
      />
    </Paper>
  );
};

const ProductOptionGroupOverrides = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const [searchMenus, setSearchMenus] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchValue);
  const {
    missingDefaultPriceFilter,
    setMissingDefaultPriceFilter,
    missingDefaultPrice,
    expandCollapseFilter,
    onExpandCollapseFilterChange,
    expanded,
    setExpanded,
  } = usePriceSchemeFilters();

  const {
    searchPricingCategories,
    setSearchPricingCategories,
    hasPricingCategoryFilterFeature,
    pricingCategoryOptions,
  } = usePricingCategoryFilter(priceSchemeId);

  const { channelOptions, searchChannels, setSearchChannels, isLoading: isChannelsLoading } = useChannelFilter();

  const modifierGroupFilters: IModifierGroupFilters = {
    name: debouncedSearchValue,
    menus: searchMenus,
    pricingCategories: searchPricingCategories,
    channels: searchChannels,
  };

  const hasActiveFilters =
    !!debouncedSearchValue ||
    searchMenus.length > 0 ||
    searchPricingCategories.length > 0 ||
    searchChannels.length > 0 ||
    missingDefaultPrice;

  const onNewProducts = useCallback(({ pages }: InfiniteData<ItemModifierGroupsResponseBody>) => {
    const flattenedPages = flattenPagesToModifiers(pages);
    const formValues = generateModifierFormDefaults(flattenedPages);
    reset(formValues, { keepDirtyValues: true, keepErrors: true });
  }, []);

  const { data: menus } = useMenus();

  const { data, isLoading, queryKey, isFetchingNextPage, fetchStatus, fetchNextPage, hasNextPage } =
    useItemModifierGroups({
      priceSchemeId,
      ...modifierGroupFilters,
      missingDefaultPrice,
      options: {
        enabled: !isChannelsLoading,
        onSuccess: onNewProducts,
      },
    });

  const tableData = (data?.pages?.flatMap(({ data }) => data) ?? []).map(mapModifierGroupToTableRow);
  const defaultValues = generateModifierFormDefaults(flattenPagesToModifiers(data?.pages));

  const {
    data: missingDefaultPriceData,
    hasNextPage: hasNextMissingDefaultPricePage,
    fetchNextPage: fetchNextMissingDefaultPricePage,
    isFetchingNextPage: isFetchingNextMissingDefaultPricePage,
  } = useItemModifierGroups({
    priceSchemeId,
    ...modifierGroupFilters,
    missingDefaultPrice: true,
    options: {
      enabled: !isChannelsLoading,
    },
  });

  useEffect(() => {
    if (hasNextMissingDefaultPricePage && !isFetchingNextMissingDefaultPricePage) {
      fetchNextMissingDefaultPricePage();
    }
  }, [hasNextMissingDefaultPricePage, isFetchingNextMissingDefaultPricePage, fetchNextMissingDefaultPricePage]);

  const missingDefaultPriceModifierIds = useMemo(
    () => new Set(flattenPagesToModifiers(missingDefaultPriceData?.pages).map(({ modifier_id }) => modifier_id)),
    [missingDefaultPriceData],
  );

  const { control, handleSubmit, reset, setValue } = useForm<FieldValues>({
    defaultValues,
    values: defaultValues,
  });

  const columns = useMemo(
    () => optionGroupCols({ control, setValue, missingDefaultPriceModifierIds }),
    [control, setValue, fetchStatus, missingDefaultPriceModifierIds],
  );

  const submitValues = useSavePriceOverrides({ priceSchemeId, queryKey, control, reset });

  return (
    <div className={styles['container']}>
      <div className={styles['controls-row']}>
        <div className={styles['search-filter-container']}>
          <SearchField
            key="bundle-modifier-search"
            isDisabled={false}
            placeholder="Search for an option group"
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
          />
          <PriceSchemeFilters
            menus={menus}
            searchMenus={searchMenus}
            setSearchMenus={setSearchMenus}
            hasPricingCategoryFilterFeature={hasPricingCategoryFilterFeature}
            pricingCategoryOptions={pricingCategoryOptions}
            searchPricingCategories={searchPricingCategories}
            setSearchPricingCategories={setSearchPricingCategories}
            channelOptions={channelOptions}
            searchChannels={searchChannels}
            setSearchChannels={setSearchChannels}
            missingDefaultPriceFilter={missingDefaultPriceFilter}
            setMissingDefaultPriceFilter={setMissingDefaultPriceFilter}
            expandCollapseFilter={expandCollapseFilter}
            setExpandCollapseFilter={onExpandCollapseFilterChange}
          />
        </div>
        <PricingSavePortal control={control} reset={reset} form={BUNDLE_MODIFIER_FORM_ID} />
      </div>
      <form id={BUNDLE_MODIFIER_FORM_ID} onSubmit={handleSubmit(submitValues)} className={styles['rules-list']}>
        <Loader loading={isLoading}>
          {tableData.length > 0 ? (
            <Table
              className={styles['table-fill-height']}
              data={tableData}
              columns={columns}
              columnHeadersToRightAlign={['adjustmentValue', 'roundingValue', 'actions']}
              getSubRows={(row) => row.modifiers}
              additionalConfig={{ state: { expanded }, onExpandedChange: setExpanded }}
              isFetchingNextPage={isFetchingNextPage}
              hasNextPage={hasNextPage}
              fetchNextPage={fetchNextPage}
            />
          ) : (
            <ModifierGroupsEmptyState hasActiveFilters={hasActiveFilters} />
          )}
        </Loader>
      </form>
    </div>
  );
};

export default ProductOptionGroupOverrides;
